import React from 'react';
import { ApprovalChainStepDescription } from './_approval-chain-step-description';
import { Text, VerticalSteps } from '@common/components';
import {
  ApprovalChainApproverType,
  ApprovalChainStepType,
  ApprovalChainType,
} from '@approvals/graphql/fragments';
import { VerticalStepStatusType } from '@common/components/vertical-steps/_vertical-step';

export type ApprovalChainStatusType =
  | 'Approved'
  | 'Rejected'
  | 'Pending'
  | 'Next'
  | 'Wait';

type StateHelperType = {
  updatedAt: (step: ApprovalChainStepType) => string | undefined;
  verticalStepStatus: VerticalStepStatusType;
  actionUsers: (step: ApprovalChainStepType) => ApprovalChainApproverType[];
};

const STATE_HELPER: Record<ApprovalChainStatusType, StateHelperType> = {
  Approved: {
    updatedAt: (step: ApprovalChainStepType) => step.updatedAt,
    verticalStepStatus: 'finish',
    actionUsers: (step: ApprovalChainStepType) => step.approvalChainApprovals,
  },
  Rejected: {
    updatedAt: (step: ApprovalChainStepType) => step.updatedAt,
    verticalStepStatus: 'error',
    actionUsers: (step: ApprovalChainStepType) => step.approvalChainRejections,
  },
  Pending: {
    updatedAt: () => undefined,
    verticalStepStatus: 'process',
    actionUsers: (step: ApprovalChainStepType) => step.approvalChainApprovers,
  },
  Next: {
    updatedAt: () => undefined,
    verticalStepStatus: 'wait',
    actionUsers: (step: ApprovalChainStepType) => step.approvalChainApprovers,
  },
  Wait: {
    updatedAt: () => undefined,
    verticalStepStatus: 'wait',
    actionUsers: (step: ApprovalChainStepType) => step.approvalChainApprovers,
  },
};

type Props = {
  approvalChain: ApprovalChainType;
  displayActionButtonsInsideStep: boolean;
  extraApprovalButton?: React.ReactNode;
};

export const ApprovalChainStatus = ({
  approvalChain,
  displayActionButtonsInsideStep,
  extraApprovalButton,
}: Props) => {
  const approvalChainSteps = approvalChain.approvalChainSteps;
  const approvalChainRejected = approvalChainSteps.some(
    (step) => step.rejected
  );
  const pendingStep = approvalChainRejected
    ? null
    : approvalChainSteps.findIndex((step) => !step.satisfied);

  const computeState = (
    index: number,
    rejected: boolean,
    satisfied: boolean
  ): ApprovalChainStatusType => {
    if (rejected) return 'Rejected';
    if (satisfied) return 'Approved';
    if (pendingStep !== null && index === pendingStep) return 'Pending';
    if (pendingStep !== null && index === pendingStep + 1) return 'Next';
    return 'Wait';
  };

  return (
    <>
      <VerticalSteps className="_approval-chain-status__vertical-steps">
        <div style={{ marginBottom: 8 }}>
          <Text size="md" weight="bold">
            Approval Chain
          </Text>
        </div>
        {approvalChainSteps
          .map((step, index: number) => {
            const stepState = computeState(
              index,
              step.rejected,
              step.satisfied
            );
            const updatedAt = STATE_HELPER[stepState].updatedAt(step);
            const verticalStepStatus =
              STATE_HELPER[stepState].verticalStepStatus;
            const actionUsers = STATE_HELPER[stepState].actionUsers(step);

            return (
              <VerticalSteps.Step key={step.id} status={verticalStepStatus}>
                <ApprovalChainStepDescription
                  actionUsers={actionUsers}
                  approvalChainStepId={step.id}
                  currentApprovers={approvalChain?.currentApprovers || []}
                  displayActionButtonsInsideStep={
                    stepState === 'Pending' && displayActionButtonsInsideStep
                  }
                  level={index + 1}
                  rejectionComment={step.approvalChainRejections[0]?.note || ''}
                  rejectionReason={
                    step.approvalChainRejections[0]?.rejectionReason
                  }
                  status={stepState}
                  updatedAt={updatedAt}
                  extraApprovalButton={extraApprovalButton}
                  stepComment={step.comments?.[0]}
                />
              </VerticalSteps.Step>
            );
          })
          .reverse()}
      </VerticalSteps>
      <style jsx>{`
        :global(
          ._approval-chain-status__vertical-steps .wk-vertical-step__icon
        ) {
          height: 32px;
          display: flex;
          align-items: center;
        }
      `}</style>
    </>
  );
};
